import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Slider from "react-slick";
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaPlay } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BreadCrumb from "../components/BreadCrumb";
import Card from "../components/Card";
import YoutubeModal from "../components/YoutubeModal";
import PayloadRichText from "../components/PayloadRichText";
import SEO from "../components/SEO";
import axios from "../lib/axios";
import { getYoutubeVideoId } from "../utils/youtube";
import { createDesigningSlug, normalizeDesigning } from "../utils/designing";

const getDesignId = (item) => item?.id || item?._id || null;

const getConfiguredRelatedItems = (item, currentId) => {
  const relatedItems = Array.isArray(item?.relatedOptions)
    ? item.relatedOptions
    : Array.isArray(item?.related_options)
      ? item.related_options
      : [];

  return relatedItems
    .map((entry) =>
      entry && typeof entry === "object" ? normalizeDesigning(entry) : null,
    )
    .filter(Boolean)
    .filter((entry) => getDesignId(entry) !== currentId)
    .slice(0, 4);
};

const HeroArrow = ({ className, onClick, direction }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${className} !flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white shadow-xl backdrop-blur transition hover:bg-black/75 before:hidden`}
    aria-label={direction === "next" ? "Next media" : "Previous media"}
  >
    {direction === "next" ? <FaChevronRight /> : <FaChevronLeft />}
  </button>
);

const PrintingDetail = () => {
  const navigate = useNavigate();
  const { identifier } = useParams();
  const [item, setItem] = useState(null);
  const [relatedItems, setRelatedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [activeHeroIndex, setActiveHeroIndex] = useState(0);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const heroSliderRef = useRef(null);

  useEffect(() => {
    const fetchItem = async () => {
      setLoading(true);
      setError(null);

      try {
        const slugResponse = await axios.get(
          `/api/printing?depth=2&where[slug][equals]=${identifier}`,
        );

        let currentItem = slugResponse.data?.docs?.[0] || null;
        if (currentItem && currentItem.enabled === false) {
          currentItem = null;
        }

        if (!currentItem && identifier) {
          try {
            const lookupResponse = await axios.get(
              "/api/printing?depth=2&limit=100&sort=-createdAt&where[enabled][not_equals]=false",
            );
            const lookupDocs = Array.isArray(lookupResponse.data?.docs)
              ? lookupResponse.data.docs
              : [];

            currentItem =
              lookupDocs.find((entry) => {
                const candidateSlug = createDesigningSlug(
                  entry?.slug || entry?.title || "",
                );
                return candidateSlug === identifier && entry.enabled !== false;
              }) || null;
          } catch (lookupError) {
            currentItem = null;
          }
        }

        if (!currentItem && identifier) {
          try {
            const idResponse = await axios.get(
              `/api/printing/${identifier}?depth=1`,
            );
            currentItem = idResponse.data || null;
            if (currentItem && currentItem.enabled === false) {
              currentItem = null;
            }
          } catch (idError) {
            currentItem = null;
          }
        }

        if (!currentItem) {
          setItem(null);
          setRelatedItems([]);
          setError("Marketing Solutions service not found");
          return;
        }

        const normalizedItem = normalizeDesigning(currentItem);
        setItem(normalizedItem);
        setActiveHeroIndex(0);
        setActiveVideoId(null);
        setShowYoutubeModal(false);

        const currentId = getDesignId(normalizedItem);
        const configuredRelatedItems = getConfiguredRelatedItems(
          currentItem,
          currentId,
        );

        if (configuredRelatedItems.length > 0) {
          setRelatedItems(configuredRelatedItems.filter((entry) => entry && entry.enabled !== false));
        } else {
          const relatedResponse = await axios.get(
            `/api/printing?depth=1&limit=4&sort=-createdAt&where[and][0][enabled][not_equals]=false${currentId ? `&where[and][1][id][not_equals]=${currentId}` : ""
            }`,
          );

          const relatedDocs = Array.isArray(relatedResponse.data?.docs)
            ? relatedResponse.data.docs
            : [];

          setRelatedItems(relatedDocs.filter((entry) => entry && entry.enabled !== false).map(normalizeDesigning));
        }
      } catch (fetchError) {
        setError(
          fetchError.response?.data?.message ||
          "Failed to load marketing/promotion details",
        );
      } finally {
        setLoading(false);
      }
    };

    if (identifier) {
      fetchItem();
    }
  }, [identifier]);

  if (loading) {
    return (
      <div className="min-h-screen homepage">
        <Navbar />
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-16 w-16 animate-spin rounded-full border-b-2 border-t-2 border-red-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen homepage">
        <Navbar />
        <SEO
          title="Not Found"
          description="The requested marketing solutions service could not be found."
          noIndex={true}
        />
        <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
          <h1 className="mb-3 text-3xl font-bold text-gray-900">
            Marketing Solutions service not found
          </h1>
          <p className="mb-6 text-gray-600">
            {error || "The requested item could not be loaded."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/printing")}
            className="rounded-lg bg-red-600 px-6 py-3 text-white transition-colors hover:bg-red-700"
          >
            Back to Printing
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const fallbackImage =
    item?.image?.cloudinary?.secure_url ||
    item?.image?.url ||
    "https://placehold.co/1200x720";
  // Card-level youtube is for the purchase card play button only — keep it out of the detail hero slider
  const cardYoutubeId = getYoutubeVideoId(item.youtube);
  const rawHeroMedia =
    Array.isArray(item?.hero_media?.slides) && item.hero_media.slides.length > 0
      ? item.hero_media.slides
      : [
        {
          media_type: "image",
          image: {
            url: fallbackImage,
          },
        },
      ];
  const heroMedia = rawHeroMedia.filter((mediaItem) => {
    if (mediaItem?.media_type !== "youtube") return true;
    const slideId = getYoutubeVideoId(mediaItem?.youtube);
    if (cardYoutubeId && slideId && slideId === cardYoutubeId) return false;
    return true;
  });
  const resolvedHeroMedia =
    heroMedia.length > 0
      ? heroMedia
      : [
        {
          media_type: "image",
          image: {
            url: fallbackImage,
          },
        },
      ];
  const heroSliderSettings = {
    dots: false,
    arrows: resolvedHeroMedia.length > 1,
    infinite: resolvedHeroMedia.length > 1,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    swipe: resolvedHeroMedia.length > 1,
    touchMove: resolvedHeroMedia.length > 1,
    adaptiveHeight: false,
    afterChange: (currentSlide) => setActiveHeroIndex(currentSlide),
    nextArrow: <HeroArrow direction="next" />,
    prevArrow: <HeroArrow direction="prev" />,
  };

  const pageUrl = `https://indorinama.com/printing/${identifier}`;
  const ogImage = item?.image?.cloudinary?.secure_url || item?.image?.url || "https://indorinama.com/marketing-og-image.jpg";

  return (
    <>
      <SEO
        title={item?.title || "Marketing Service"}
        description={item?.short_description || item?.description?.slice(0, 160) || `Professional ${item?.title || 'marketing'} services by Indorinama`}
        keywords={`${item?.title || 'marketing'}, ${item?.collection || ''}, marketing promotion, advertising, Indorinama`}
        url={pageUrl}
        image={ogImage}
        type="product"
      />
      <div className="min-h-screen homepage">
        <Navbar subMenu={false} />

        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-6">
            <BreadCrumb currentPage={item.title} />
          </div>

          <button
            type="button"
            onClick={() => navigate("/printing")}
            className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-red-600"
          >
            <FaArrowLeft className="text-xs" />
            Back to Printing
          </button>

          <div className="grid gap-8 lg:grid-cols-[minmax(320px,420px)_minmax(0,1.6fr)]">
            <aside className="order-1 lg:sticky lg:top-24 lg:order-2 lg:self-start">
              <div className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5">
                <div className="relative mb-6 -ml-8 w-max">
                  <div className="rounded-r-lg bg-red-600 py-1.5 pl-8 pr-5 text-sm font-bold uppercase tracking-[0.2em] text-white shadow-md">
                    Purchase
                  </div>
                  <svg
                    className="absolute -bottom-3 left-0 h-3 w-4 text-red-800"
                    fill="currentColor"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    <polygon points="0,0 100,0 100,100" />
                  </svg>
                </div>
                <Card
                  copyLink={false}
                  data={item}
                  category="printing"
                />
              </div>
            </aside>

            <div className="order-2 overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-black/5 lg:order-1">
              <div className="relative overflow-hidden bg-gray-950">
                <Slider ref={heroSliderRef} {...heroSliderSettings}>
                  {resolvedHeroMedia.map((mediaItem, index) => {
                    const mediaType = mediaItem?.media_type || "image";
                    const videoId =
                      mediaType === "youtube"
                        ? getYoutubeVideoId(mediaItem?.youtube)
                        : null;
                    const slideImage =
                      mediaType === "youtube" && videoId
                        ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
                        : mediaItem?.image?.url || fallbackImage;

                    return (
                      <div key={`${mediaType}-${index}`}>
                        <div className="relative h-[280px] sm:h-[380px]">
                          <img
                            src={slideImage}
                            alt={`${item.title} ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />

                          {videoId && (
                            <button
                              type="button"
                              onClick={() => {
                                setActiveVideoId(videoId);
                                setShowYoutubeModal(true);
                              }}
                              className="absolute inset-0 flex items-center justify-center"
                              aria-label={`Play ${item.title} video`}
                            >
                              <span className="inline-flex items-center gap-3 rounded-full border border-white/25 bg-white/15 px-5 py-3 text-sm font-semibold text-white shadow-2xl backdrop-blur-md transition hover:scale-[1.02] hover:bg-white/20">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-white shadow-lg">
                                  <FaPlay className="ml-0.5" />
                                </span>
                                Watch Video
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Slider>

                {resolvedHeroMedia.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/35 px-3 py-2 backdrop-blur-md">
                    {resolvedHeroMedia.map((_, index) => (
                      <button
                        key={`hero-dot-${index}`}
                        type="button"
                        onClick={() => heroSliderRef.current?.slickGoTo(index)}
                        className={`h-2.5 rounded-full transition-all ${activeHeroIndex === index
                            ? "w-8 bg-white"
                            : "w-2.5 bg-white/50 hover:bg-white/80"
                          }`}
                        aria-label={`Go to media ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="p-6 sm:p-8">
                <h1 className="mb-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  {item.title}
                </h1>

                <p className="text-base leading-7 text-gray-600">
                  {item.description}
                </p>

                {item.sub_description && (
                  <div className="mt-8 rounded-2xl bg-gray-50 p-5">
                    <div className="relative mb-6 -ml-8 w-max">
                      <div className="rounded-r-lg bg-red-600 py-1.5 pl-8 pr-5 text-sm font-bold uppercase tracking-[0.2em] text-white shadow-md">
                        More Details
                      </div>
                      <svg
                        className="absolute -bottom-3 left-0 h-3 w-4 text-red-800"
                        fill="currentColor"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                      >
                        <polygon points="0,0 100,0 100,100" />
                      </svg>
                    </div>
                    <PayloadRichText content={item.sub_description} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {relatedItems.length > 0 && (
            <section className="mt-12">
              <div className="mb-6 flex items-end justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-700">
                    More Options
                  </p>
                  <h2 className="mt-2 text-2xl font-bold text-gray-900">
                    Related Marketing Solutions Services
                  </h2>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
                {relatedItems.map((relatedItem) => (
                  <Card
                    key={relatedItem.id || relatedItem._id || relatedItem.slug}
                    data={relatedItem}
                    category="printing"
                    navigateToDetails
                    id={`marketing-${relatedItem.slug}`}
                  />
                ))}
              </div>
            </section>
          )}
        </div>

        <YoutubeModal
          isOpen={showYoutubeModal}
          onClose={() => {
            setShowYoutubeModal(false);
            setActiveVideoId(null);
          }}
          videoId={activeVideoId}
        />

        <Footer />
      </div>
    </>
  );
};

export default PrintingDetail;
